import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Message Sent Successfully!",
        description: "We'll get back to you within 24 hours.",
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-[#1e3a8a] to-[#2563eb]">
        <div className="max-w-5xl mx-auto text-center text-white">
          <h1 className="text-6xl font-bold mb-6">Contact Us</h1>
          <p className="text-2xl text-gray-100">
            Let's Start Your Solar Journey
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-[#1e3a8a] mb-8">
                Get in Touch
              </h2>
              <p className="text-xl text-gray-700 mb-12 leading-relaxed">
                Interested in our products or need an EPC consultation? Reach out to us 
                and let's discuss how BRISE can power your future.
              </p>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-[#fbbf24] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-7 w-7 text-[#1e3a8a]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">Our Office</h3>
                    <p className="text-lg text-gray-700">
                      Dehradun, Uttarakhand<br />
                      India
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-[#fbbf24] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-7 w-7 text-[#1e3a8a]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">Phone</h3>
                    <p className="text-lg text-gray-700">+91 6396455851</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-[#fbbf24] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-7 w-7 text-[#1e3a8a]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">Email</h3>
                    <p className="text-lg text-gray-700">pulkit.garg@briseenergy.com</p>
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="mt-12">
                <img 
                  src="https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?w=800&q=85" 
                  alt="Solar Installation" 
                  className="rounded-2xl shadow-lg"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 p-10 rounded-2xl shadow-lg">
              <h3 className="text-3xl font-bold text-[#1e3a8a] mb-8">Send Us a Message</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name"
                    className="w-full h-12 text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="w-full h-12 text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full h-12 text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Interested In *
                  </label>
                  <Select value={formData.service} onValueChange={handleServiceChange} required>
                    <SelectTrigger className="w-full h-12 text-base">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manufacturing">Manufacturing & Supply</SelectItem>
                      <SelectItem value="epc">EPC Projects</SelectItem>
                      <SelectItem value="om">Operations & Maintenance</SelectItem>
                      <SelectItem value="consultation">Consultation</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project..."
                    className="w-full min-h-32 text-base"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 bg-[#1e3a8a] hover:bg-[#2563eb] text-white text-lg font-semibold transition-all duration-300"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
